import React, { useEffect, useState } from 'react';
import { push } from 'connected-react-router';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import WhsManagementGrid from 'components/WhsManagement';
import LinkedAccountsModal from 'components/Modals/LinkedAccountsModal';
import AccessControl from 'components/AccessControl';
import ActivityPerms from 'components/AccessControl/permissions.json';
import whsManagementColumnDefs from 'components/WhsManagement/whsManagementColumnDefs.js';
import whsManagementDraftColumnDefs from 'components/WhsManagement/whsManagementDraftColumnDefs.js';
import whsManagementUnpublishedColumnDefs from 'components/WhsManagement/whsManagementUnpublishedColumnDefs.js';
import whsManagementMissingColumnDefs from 'components/WhsManagement/whsManagementMissingColumnDefs.js';

import {
  getWhdStatements,
  newGridCriteria,
  exportWhsManagementToExcel
} from 'actions';
import { getLinkedAccounts } from '../../actions/withholding/whsManagement';

const processData = (responseData) => {
  const data = [];
  const buildingMessage = 'Building, This may take a few minutes...';

  if (responseData.results != null) {
    responseData.results.forEach((item) => {
      const datum = {
        stmtId: item?.stmtId,
        customerDbId: item?.customerDbId,
        customerId: item?.customerId,
        customerName: item?.customerName,
        // eslint-disable-next-line no-nested-ternary
        customerIsActive: item?.customerIsActive != null ? item?.customerIsActive ? 'Active' : 'Inactive' : 'N/A',
        accountId: item?.accountId,
        accountNumber: item?.accountNumber,
        accountName: item?.accountName,
        // eslint-disable-next-line no-nested-ternary
        accountIsActive: item?.accountIsActive != null ? item?.accountIsActive ? 'Active' : 'Inactive' : 'N/A',
        dateFrom: (item?.dateFrom == null || item?.dateFrom === '') ? 'N/A' : moment(item?.dateFrom).format('DD MMM, YYYY'),
        dateTo: (item?.dateTo == null || item?.dateTo === '') ? 'N/A' : moment(item?.dateTo).format('DD MMM, YYYY'),
        published: item?.published,
        whsName: item?.pendingJob === true ? buildingMessage : item?.whsName,
        statementType: ((v => (v === 'Alt' ? 'Alternative' : v))(item?.statementType?.replace(' Withholding Statement', ''))),
        // eslint-disable-next-line no-nested-ternary
        isValid: (item?.statementValid == null || item?.statementValid === '') ? 'N/A' : item?.statementValid ? 'Valid' : 'Invalid',
        allocation: (item?.allocation == null || item?.allocation === '') ? 'N/A' : (item.allocation * 100).toFixed(item.decimalPrecision).toString().concat('%'),
        numBeneficialOwners: item.numBeneficialOwners,
        formsMissing: (item?.formsMissing == null || item?.formsMissing === '') ? 'N/A' : item?.formsMissing,
        formsInvalid: (item?.formsInvalid == null || item?.formsInvalid === '') ? 'N/A' : item?.formsInvalid,
        formsValid: (item?.formsValid == null || item?.formsValid === '') ? 'N/A' : item?.formsValid,
        formId: item?.formId,
        formType: item?.formType,
        workflowStage: item?.workflowStage,
        // eslint-disable-next-line no-nested-ternary
        formIsValid: (item?.formIsValid == null || item?.formIsValid === '') ? 'N/A' : item?.formIsValid ? 'Valid' : 'Invalid',
        chapter3Status: item?.chapter3Status,
        expectedWithholdingStatementType: item?.expectedWithholdingStatementType,
        version: item?.version,
        linkedAccounts: item?.linkedAccounts,
        pendingJob: item?.pendingJob === true
      };
      data.push(datum);
    });
  }
  const returnData = {
    results: data,
    totalCount: responseData.totalCount
  };
  return returnData;
};

const fetchGridData = (gridCriteria, getWhdStatementsTable) => {
  const { pageIndex, pageSize, activeTab, sortBy, filters, asOfDate } = gridCriteria;

  const sortById = sortBy !== null ? sortBy[0]?.id : null;
  let sortDirection;
  if (sortById !== null) {
    sortDirection = sortBy[0]?.desc ? 'desc' : 'asc';
  } else {
    sortDirection = null;
  }

  getWhdStatementsTable(
    pageIndex,
    pageSize,
    sortById,
    sortDirection,
    filters,
    activeTab,
    asOfDate,
    processData
  );
};

const WhsManagement = () => {
  const dispatch = useDispatch();
  const { gridCriteria, loading, data, actions } = useSelector(state => state.whsManagement);
  const { isExportingToExcel } = useSelector(state => state.forms);

  const [activeTab, setActiveTab] = useState(gridCriteria.activeTab > 0 ? gridCriteria.activeTab : 0);

  const getWhdStatementsTable = (pageIndex, pageSize, sortBy, sortDirection, filters, tab, asOfDate, processDataFunc) => dispatch(getWhdStatements(pageIndex + 1, pageSize, sortBy, sortDirection, filters, tab, asOfDate, processDataFunc));
  const saveNewGridCriteria = criteria => dispatch(newGridCriteria(criteria));
  const downloadWhsManagementToExcel = criteria => dispatch(exportWhsManagementToExcel(criteria));
  const openLinkedAccountModal = accountId => dispatch(getLinkedAccounts(accountId));

  const saveGridAndRefresh = (criteria) => {
    saveNewGridCriteria(criteria);
    fetchGridData(criteria, getWhdStatementsTable);
  };

  const tabs = [
    {
      title: 'Published',
      index: 0,
      field: 'published',
      id: 'tab-published',
      colDefs: whsManagementColumnDefs()
    },
    {
      title: 'Drafts',
      index: 1,
      field: 'drafts',
      id: 'tab-drafts',
      colDefs: whsManagementDraftColumnDefs(openLinkedAccountModal)
    },
    {
      title: 'Unpublished',
      index: 2,
      field: 'unpublished',
      id: 'tab-unpublished',
      colDefs: whsManagementUnpublishedColumnDefs(openLinkedAccountModal)
    },
    {
      title: 'Missing',
      index: 3,
      field: 'missing',
      id: 'tab-missing',
      colDefs: whsManagementMissingColumnDefs()
    }
  ];

  const isNoData = (!data || !data.length || data.length === 0);

  const navigateToImyStructure = (accountId, versionNo) => {
    if (versionNo) {
      dispatch(push(`/w8imystructure/${ accountId }/${ versionNo }`));
    } else {
      dispatch(push(`/w8imystructure/${ accountId }`));
    }
  };

  const navigateToWithholdingStatement = (accountId, versionNo) => {
    if (versionNo) {
      dispatch(push(`/whd-statement/${ accountId }/${ versionNo }`));
    } else {
      dispatch(push(`/whd-statement/${ accountId }`));
    }
  };

  const navigateToForm = (accountId, formId, formType) => {
    if (formType === 'W-8IMY Oct-2021') {
      dispatch(push(`/forms/w8imy_2021/${ formId }/?account=${ accountId }`));
    }
    if (formType === 'W-8IMY Jun-2017') {
      dispatch(push(`/forms/w8imy_2017/${ formId }/?account=${ accountId }`));
    }
    if (formType === 'W-8IMY Sep-2016') {
      dispatch(push(`/forms/w8imy/${ formId }/?account=${ accountId }`));
    }
  };

  const selectTab = (tabIndex) => {
    if (tabIndex !== gridCriteria.activeTab) {
      gridCriteria.activeTab = tabs[tabIndex].index;
      gridCriteria.pageIndex = 0;
      gridCriteria.columnDefs = tabs[tabIndex].colDefs;
      gridCriteria.filters = [];
      gridCriteria.sortBy = [];
      if (tabIndex !== 0) {
        gridCriteria.asOfDate = undefined;
      }
      setActiveTab(tabs[tabIndex].index);
      saveGridAndRefresh(gridCriteria);
    }
  };

  useEffect(() => {
    fetchGridData(gridCriteria, getWhdStatementsTable);
  }, []);

  return (
    <div className="container whs-managment-results-panel">
      <div className="panel no-border">
        <nav className="panel-nav">
          <ul className="tab">
            { tabs != null && tabs.map(tab => (
              <li
                key={ tab.index }
                className={ `tab-item ${ tab.index === activeTab ? 'active' : '' }` }
              >
                <button
                  type="button"
                  id={ tab.id }
                  tabIndex={ tab.index }
                  onClick={ () => selectTab(tab.index) }
                >{ tab.title }</button>
              </li>
            )) }
          </ul>
          <aside className="tool-panel">
            <button
              type="button"
              className="btn btn-secondary"
              title="Refresh Grid"
              onClick={ () => fetchGridData(gridCriteria, getWhdStatementsTable) }
            >
              <span className="icon icon-refresh" />
            </button>
            <AccessControl allowedPermissions={ [ActivityPerms.multiTierMgrGenerateWhsManagementExcel] }>
              <button
                type="button"
                title="Generate report"
                className="btn"
                id="whs-management-generate-report"
                onClick={ () => downloadWhsManagementToExcel(gridCriteria) }
                disabled={ isNoData || isExportingToExcel }
              >
                <span className={ `icon ${ isExportingToExcel ? 'loading' : 'icon-share' } mr` } />
                <span>Generate report</span>
              </button>
            </AccessControl>
          </aside>
        </nav>
        <div className="panel-body bordered">
          <WhsManagementGrid
            gridCriteria={ gridCriteria }
            loading={ loading }
            data={ data }
            actions={ actions(navigateToWithholdingStatement, navigateToImyStructure, navigateToForm) }
            onGridCriteriaChanged={ saveGridAndRefresh }
          />
          <LinkedAccountsModal />
        </div>
      </div>
    </div>
  );
};

export default WhsManagement;
